/**
 * BT-038 — Présentation interactive (adaptateur BT-033).
 */
import {
  DEMO_CLIENT_SECTIONS,
  PHASE1_DEMO_LIMITS_FR,
  PHASE1_DEMO_LIMITS_EN,
  PHASE2_PREVIEW_FR,
  PHASE2_PREVIEW_EN,
} from "@/content/demoClientGuide033.js";

export const PRESENTATION_BT_ID = "BT-GSD-KARATE-PRESENTATION-INTERACTIVE-NOTES-VOCALES-038";
export const PRESENTATION_STORAGE_KEY = "karate-interactive-presentation-038-v1";

export const NOTE_TYPES = {
  fr: [
    { id: "question", label: "Question" },
    { id: "correction", label: "Correction" },
    { id: "improvement", label: "Amélioration" },
    { id: "decision", label: "Décision" },
    { id: "review", label: "À revoir" },
  ],
  en: [
    { id: "question", label: "Question" },
    { id: "correction", label: "Correction" },
    { id: "improvement", label: "Improvement" },
    { id: "decision", label: "Decision" },
    { id: "review", label: "To review" },
  ],
};

/** Durées approximatives par bloc (minutes). */
const SECTION_DURATIONS = {
  express: {
    public: "3–4 min",
    coquille: "3–4 min",
    admin: "4–5 min",
    gestionnaire: "3 min",
    joueur: "3–4 min",
    formation: "2–3 min",
    limites: "3 min",
  },
  full: {
    public: "8–10 min",
    coquille: "7–9 min",
    admin: "12–15 min",
    gestionnaire: "5–6 min",
    joueur: "6–8 min",
    formation: "4–5 min",
    limites: "4–5 min",
  },
};

/** Écrans clés en mode express (indices dans section.steps). */
const EXPRESS_SCREEN_INDICES = {
  public: [0, 7],
  coquille: [0, 2, 5],
  admin: [0, 8, 9, 10],
  gestionnaire: [0, 1, 2],
  joueur: [0, 3],
  formation: [0],
  limites: [0, 1],
};

const SECTION_OBJECTIVES = {
  fr: {
    public: "Montrer le portail public, le jeu initial minimal BT-046 et la demande de compte.",
    coquille: "Expliquer FR/EN, Aide, Formation et les 3 espaces GSD.",
    admin: "Parcourir l'administration Hanshi, Shotokan et Documents & Preuves.",
    gestionnaire: "Contraster Sensei et Sempai — permissions et limites.",
    joueur: "Montrer la simplicité Étudiant (7 onglets) et Parent.",
    formation: "Différencier Formation (apprendre) vs cette Présentation (démo + feedback).",
    limites: "Clôturer avec limites Phase 1 et aperçu Phase 2 — sans promesse de date.",
  },
  en: {
    public: "Show the public portal, minimal BT-046 initial dataset and account request.",
    coquille: "Explain FR/EN, Help, Training and the 3 GSD spaces.",
    admin: "Walk through Hanshi admin, Shotokan and Documents & Evidence.",
    gestionnaire: "Contrast Sensei and Sempai — permissions and limits.",
    joueur: "Show Student simplicity (7 tabs) and Parent space.",
    formation: "Differentiate Training (learn) vs this Presentation (demo + feedback).",
    limites: "Close with Phase 1 limits and Phase 2 preview — no date promises.",
  },
};

const SECTION_POINTS = {
  fr: {
    public: ["Bandeau [DÉMO] visible", "10 dojos = objectif après fichier client", "Connexion comptes démo"],
    coquille: ["Aide = écran courant", "Formation = par rôle", "Header Shotokan AAMC"],
    admin: ["6 groupes navigation", "D&P dans Shotokan seulement", "Import contrôlé sans doc réel"],
    gestionnaire: ["Sensei valide", "Sempai ne valide pas les titres", "Accordéon navigation"],
    joueur: ["7 onglets étudiant", "Espace parent séparé", "Pas d'écrans admin"],
    formation: ["4 parcours", "Liens vers écrans", "Complément à l'Aide"],
    limites: ["Seed minimal BT-046", "Pas de backend documents", "Import massif à venir"],
  },
  en: {
    public: ["Visible [DEMO] banner", "10 dojos = goal after client file", "Demo account sign-in"],
    coquille: ["Help = current screen", "Training = by role", "Shotokan AAMC header"],
    admin: ["6 nav groups", "D&E inside Shotokan only", "Controlled import, no real doc"],
    gestionnaire: ["Sensei validates", "Sempai does not validate titles", "Accordion navigation"],
    joueur: ["7 student tabs", "Separate parent space", "No admin screens"],
    formation: ["4 paths", "Links to screens", "Complement to Help"],
    limites: ["Minimal BT-046 seed", "No documents backend", "Bulk import later"],
  },
};

export const PRESENTATION_CHECKLIST = {
  fr: [
    "Choisir FR ou EN",
    "Ouvrir le rôle Admin",
    "Choisir express ou complet",
    "Vérifier que les données [DÉMO] sont bien expliquées",
    "Ne pas présenter les données comme réelles",
    "Préparer les questions",
    "Garder les limites Phase 1 visibles",
  ],
  en: [
    "Choose FR or EN",
    "Open the Admin role",
    "Choose express or full demo",
    "Make sure [DEMO] data is clearly explained",
    "Do not present demo data as real",
    "Prepare questions",
    "Keep Phase 1 limits visible",
  ],
};

export function getPresentationUi(locale) {
  const isEn = locale === "en";
  return {
    pageTitle: isEn ? "Interactive presentation" : "Présentation interactive",
    pageSubtitle: isEn
      ? "Guided client demo, presenter notes and improvement report — admin only."
      : "Démo client guidée, notes présentateur et rapport d'amélioration — admin seulement.",
    intro: isEn
      ? "Use this tool during a meeting with Gilles or a client: pick a track, follow the 7 demo blocks, open real screens, capture notes and export an improvement report."
      : "Utilisez cet outil lors d'une rencontre avec Gilles ou un client : choisissez un parcours, suivez les 7 blocs, ouvrez les écrans réels, prenez des notes et exportez un rapport d'amélioration.",
    trackExpress: isEn ? "Express demo — 25 minutes" : "Démo express — 25 minutes",
    trackFull: isEn ? "Full demo — 50 to 65 minutes" : "Démo complète — 50 à 65 minutes",
    trackExpressHint: isEn ? "Key screens per block — fast client overview." : "Écrans clés par bloc — vue client rapide.",
    trackFullHint: isEn ? "All BT-033 screens — thorough walkthrough." : "Tous les écrans BT-033 — parcours complet.",
    stepsTitle: isEn ? "Demo steps" : "Étapes de démo",
    presenterMode: isEn ? "Presenter view" : "Mode présentateur",
    startTour: isEn ? "Start guided tour" : "Démarrer le tour guidé",
    exitTour: isEn ? "Exit tour" : "Quitter le tour",
    prevStep: isEn ? "Previous step" : "Étape précédente",
    nextStep: isEn ? "Next step" : "Étape suivante",
    markComplete: isEn ? "Mark as completed" : "Marquer comme complété",
    resetDemo: isEn ? "Reset demo" : "Réinitialiser la démo",
    openScreen: isEn ? "Open this screen" : "Ouvrir cet écran",
    backToPresentation: isEn ? "Back to presentation" : "Retour à la présentation",
    duration: isEn ? "Approx. duration" : "Durée approximative",
    objective: isEn ? "Objective" : "Objectif",
    screens: isEn ? "Screens to show" : "Écrans à montrer",
    keyPoints: isEn ? "Key points" : "Points clés",
    sayReady: isEn ? "Ready to say" : "Message prêt à dire",
    limitsReminder: isEn ? "Limits to remember" : "Limites à rappeler",
    progress: isEn ? "Progress" : "Progression",
    stepActive: isEn ? "Active step" : "Étape active",
    stepDone: isEn ? "Completed" : "Complétée",
    notesTitle: isEn ? "Notes, questions and comments" : "Notes, questions et commentaires",
    noteType: isEn ? "Note type" : "Type de note",
    noteText: isEn ? "Your note" : "Votre note",
    addNote: isEn ? "Add note" : "Ajouter une note",
    editNote: isEn ? "Edit" : "Modifier",
    deleteNote: isEn ? "Delete" : "Supprimer",
    saveNote: isEn ? "Save note" : "Enregistrer la note",
    cancelEdit: isEn ? "Cancel" : "Annuler",
    dictateNote: isEn ? "Dictate a note" : "Dicter une note",
    stopDictation: isEn ? "Stop dictation" : "Arrêter la dictée",
    dictationActive: isEn
      ? "Dictation active — click Stop dictation to finish."
      : "Dictée active — cliquez sur Arrêter la dictée pour terminer.",
    dictationListening: isEn ? "Listening" : "En écoute",
    voiceUnavailable: isEn
      ? "Voice dictation is not available in this browser."
      : "Dictée vocale non disponible dans ce navigateur.",
    generateReport: isEn ? "Generate improvement report" : "Générer le rapport d'amélioration",
    copyReport: isEn ? "Copy report" : "Copier le rapport",
    exportMarkdown: isEn ? "Export Markdown" : "Exporter Markdown",
    exportJson: isEn ? "Export JSON" : "Exporter JSON",
    resetNotes: isEn ? "Reset notes" : "Réinitialiser les notes",
    checklistTitle: isEn ? "Pre-presentation checklist" : "Checklist avant présentation",
    limitsTitle: isEn ? "Phase 1 limits" : "Limites Phase 1",
    phase2Title: isEn ? "Phase 2 preview" : "Aperçu Phase 2",
    reportTitle: isEn ? "Improvement report" : "Rapport d'amélioration",
    noNotes: isEn ? "No notes yet for this step." : "Aucune note pour cette étape.",
    tourHint: isEn
      ? "Tour mode: open the suggested screen, then return here to continue."
      : "Mode tour : ouvrez l'écran suggéré, puis revenez ici pour continuer.",
    copied: isEn ? "Report copied to clipboard." : "Rapport copié dans le presse-papier.",
    nextAction: isEn ? "Next action" : "Prochaine action",
  };
}

function mapScreen(step, locale) {
  const isEn = locale === "en";
  return {
    route: step.route,
    label: isEn ? step.labelEn ?? step.labelFr : step.labelFr,
    say: isEn ? step.sayEn ?? step.sayFr : step.sayFr,
    show: isEn ? step.showEn ?? step.showFr : step.showFr,
    auth: step.auth,
  };
}

export function getPresentationBlocks(trackId, locale) {
  const isEn = locale === "en";
  const limits = isEn ? PHASE1_DEMO_LIMITS_EN : PHASE1_DEMO_LIMITS_FR;
  const pick = trackId === "express" ? EXPRESS_SCREEN_INDICES : null;

  return DEMO_CLIENT_SECTIONS.map((section) => {
    const indices = pick?.[section.id];
    const steps =
      indices != null
        ? indices.map((i) => section.steps[i]).filter(Boolean)
        : section.steps;

    const screens = steps.map((s) => mapScreen(s, locale));
    const primarySay = screens[0]?.say ?? "";

    return {
      id: section.id,
      title: isEn ? section.titleEn : section.titleFr,
      duration: SECTION_DURATIONS[trackId]?.[section.id] ?? "—",
      objective: SECTION_OBJECTIVES[locale]?.[section.id] ?? "",
      points: SECTION_POINTS[locale]?.[section.id] ?? [],
      limits: section.id === "limites" ? limits.slice(0, 4) : limits.slice(0, 2),
      screens,
      sayMessage: primarySay,
      zone: section.id,
      sectionAuth: section.auth,
    };
  });
}

export function getPhase2Preview(locale) {
  return locale === "en" ? PHASE2_PREVIEW_EN : PHASE2_PREVIEW_FR;
}

export function getPhase1Limits(locale) {
  return locale === "en" ? PHASE1_DEMO_LIMITS_EN : PHASE1_DEMO_LIMITS_FR;
}

export function getNoteTypeLabel(typeId, locale) {
  const types = NOTE_TYPES[locale === "en" ? "en" : "fr"];
  return types.find((t) => t.id === typeId)?.label ?? typeId;
}

export function buildImprovementReport({ locale, trackId, blocks, notes, completedIds }) {
  const isEn = locale === "en";
  const trackLabel =
    trackId === "express"
      ? isEn
        ? "Express demo — 25 minutes"
        : "Démo express — 25 minutes"
      : isEn
        ? "Full demo — 50 to 65 minutes"
        : "Démo complète — 50 à 65 minutes";

  const dateStr = new Date().toLocaleString(isEn ? "en-CA" : "fr-CA");
  const lines = [
    `# ${isEn ? "GSD Karate Phase 1 — Improvement report" : "GSD Karaté Phase 1 — Rapport d'amélioration"}`,
    "",
    `- **${isEn ? "Date" : "Date"}:** ${dateStr}`,
    `- **${isEn ? "Track" : "Parcours"}:** ${trackLabel}`,
    `- **${isEn ? "Language" : "Langue"}:** ${isEn ? "EN" : "FR"}`,
    `- **${isEn ? "Completed steps" : "Étapes complétées"}:** ${completedIds.length} / ${blocks.length}`,
    "",
    `## ${isEn ? "Phase 1 limits reminder" : "Rappel limites Phase 1"}`,
    "",
    ...getPhase1Limits(locale).map((l) => `- ${l}`),
    "",
    `## ${isEn ? "Steps summary" : "Résumé des étapes"}`,
    "",
  ];

  for (const block of blocks) {
    const done = completedIds.includes(block.id);
    lines.push(`### ${block.title} ${done ? (isEn ? "(completed)" : "(complétée)") : ""}`);
    lines.push(`- ${isEn ? "Objective" : "Objectif"}: ${block.objective}`);
    const stepNotes = notes.filter((n) => n.stepId === block.id);
    if (stepNotes.length === 0) {
      lines.push(`- ${isEn ? "No notes" : "Aucune note"}`);
    } else {
      lines.push("");
      for (const type of NOTE_TYPES[isEn ? "en" : "fr"]) {
        const typed = stepNotes.filter((n) => n.type === type.id);
        if (typed.length === 0) continue;
        lines.push(`**${type.label}:**`);
        for (const n of typed) {
          lines.push(`- ${n.text} _(${new Date(n.createdAt).toLocaleString(isEn ? "en-CA" : "fr-CA")})_`);
        }
        lines.push("");
      }
    }
    lines.push("");
  }

  return lines.join("\n");
}

export function buildImprovementReportJson(payload) {
  return JSON.stringify(
    {
      bt: PRESENTATION_BT_ID,
      generatedAt: new Date().toISOString(),
      ...payload,
    },
    null,
    2,
  );
}

export function isSpeechRecognitionSupported() {
  // Réexport BT-039 — implémentation canonique dans speechDictation.js
  if (typeof window === "undefined") return false;
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}
